import { Api } from "@/config";
import { VuexModule, Module, Mutation, Action } from "vuex-module-decorators";
import Project from "@/models/project/project";
import Request from "@/models/checkRequest/request";
import Response from "@/models/checkRequest/response";
import RequestService from "@/services/requestService";
import {
  SET_HTTP_METHOD,
  CLEAR_REQUESTS,
  SET_ACTIVE_FILTER,
  SET_SEARCH_QUERY,
  ADD_PROJECT,
  ADD_REQUESTS,
  SET_REQUEST_PENDING,
  DELETE_REQUEST,
  ADD_REQUEST_RESULT,
  SET_ACTIVE_REQUEST_ID,
  SET_REQUEST_READY,
  SET_QUEUE_BUSY,
  UPDATE_URL,
} from "@/store/mutation-types";

@Module({ namespaced: true })
export default class Projects extends VuexModule {
  private project = new Project();
  public projects: Array<Project> = [this.project];
  public activeProjectId = this.project.id;
  public activeRequestId = "";
  public activeFilter = "All";
  public queueBusy = false;
  public searchQuery = "";

  //#region Actions
  @Action
  public send(postModel: Request): Promise<void> {
    return new Promise((resolve, reject) => {
      //
      // Set the flag isPending to true
      this.context.commit(SET_REQUEST_PENDING, postModel);
      //
      // Send the request to the API
      Api.post(`urls`, postModel)
        .then(
          (response) => {
            // Fetch the result from the response
            let resultPayload = {
              id: postModel.id,
              hasResults: response.data.hasResults,
              results: response.data.results,
            };

            this.context.commit(ADD_REQUEST_RESULT, resultPayload);
          },
          () => {
            //
            // Resolve the promise
            // so the queue continues
            reject();
          }
        )
        // Promise.prototype.finally() isn't supported in IE11 + Safari
        // Using babel-polyfill (see main.js) to fix this
        // see: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Promise/finally
        .finally(() => {
          // Set global flags
          this.context.commit(SET_REQUEST_READY, postModel);

          // Resolve the promise
          resolve();
        });
    });
  }

  // todo: refactor to mutation
  @Action
  public setActiveRequestById(id: string) {
    const request = this.requests.find((request) => request.id == id);

    if (request) {
      this.context.commit(SET_ACTIVE_REQUEST_ID, request.id);
    }
  }

  //#endregion

  //#region Getters
  //
  // Return the selected Project
  // if the activeProjectId is set
  get activeProject(): Project | undefined {
    return this.activeProjectId
      ? this.projects.find((cr) => cr.id === this.activeProjectId)
      : undefined;
  }

  //
  // Return the selected Request
  // if the activeRequestId is set
  get activeRequest(): Request | undefined {
    return this.activeRequestId
      ? this.requests.find((cr) => cr.id === this.activeRequestId)
      : undefined;
  }

  get requests(): Array<Request> {
    return this.activeProject ? this.activeProject.requests : [];
  }

  get requestsError(): Array<Request> {
    return this.requests.filter((request) => {
      if (request.results.length == 0) return false;

      return request.isReady === true && request.results[0].errorReasonPhrase;
    });
  }

  get requestsInvalid(): Array<Request> {
    return this.requests.filter((request) => request.isValid === false);
  }

  get requestsPending(): Array<Request> {
    return this.requests.filter(
      (request) => request.isPending === true && request.isValid
    );
  }

  get requestsUnchecked(): Array<Request> {
    return this.requests.filter(
      (request) => !request.isPending && !request.isReady
    );
  }

  get requestsReady(): Array<Request> {
    return this.requests.filter((request) => request.isReady === true);
  }

  get requestsSuccess(): Array<Request> {
    return this.requests.filter((request) =>
      RequestService.RequestHasResults(request, 200, 300)
    );
  }

  get requestsMoved(): Array<Request> {
    return this.requests.filter((request) =>
      RequestService.RequestHasResults(request, 300, 400)
    );
  }

  get requestsClientError(): Array<Request> {
    return this.requests.filter((request) =>
      RequestService.RequestHasResults(request, 400, 500)
    );
  }

  get requestsServerError(): Array<Request> {
    return this.requests.filter((request) =>
      RequestService.RequestHasResults(request, 500, 600)
    );
  }

  get filteredRequests(): Array<Request> {
    let requests = [];
    switch (this.activeFilter) {
      case "-1":
        requests = this.requestsError;
        break;
      case "0":
        requests = this.requestsInvalid;
        break;
      case "200":
        requests = this.requestsSuccess;
        break;
      case "300":
        requests = this.requestsMoved;
        break;
      case "400":
        requests = this.requestsClientError;
        break;
      case "500":
        requests = this.requestsServerError;
        break;
      case "Ready":
        requests = this.requestsReady;
        break;
      case "Pending":
        requests = this.requestsPending;
        break;
      default:
        requests = this.requests;
        break;
    }

    if (this.searchQuery) {
      return requests.filter((r) => r.url.includes(this.searchQuery));
    }

    return requests;
  }
  //#endregion

  //#region Mutations
  // Delete a Project (found by it's id)
  // from the store completely.
  @Mutation
  public deleteProject(id: string): void {
    let index = this.projects.findIndex((c) => c.id === id);

    // if the deleted Project === activeProjectId: set to ''
    if (id === this.activeProjectId) {
      this.activeProjectId = "";
    }

    if (index > -1) {
      this.projects.splice(index, 1);
    }
  }

  @Mutation
  public setActiveProjectById(id: string) {
    const project = this.projects.find((project) => project.id == id);

    if (project) {
      this.activeProjectId = project.id;
    }
  }

  @Mutation
  public [CLEAR_REQUESTS](): void {
    const activeProject = this.activeProjectId
      ? this.projects.find((cr) => cr.id === this.activeProjectId)
      : undefined;
    if (activeProject) {
      activeProject.requests = [];
    }
    this.activeFilter = "All";
  }

  @Mutation
  public [SET_ACTIVE_FILTER](newFilter: string) {
    this.activeFilter = newFilter;
  }

  @Mutation
  public [SET_SEARCH_QUERY](query: string) {
    this.searchQuery = query;
  }

  @Mutation
  public [ADD_PROJECT](newProject: Project) {
    this.projects.push(newProject);
  }

  //
  // Requests
  //
  // This will add an array of CheckRequets
  // to the store. If there are any requests in the store
  // already, the new requests will be added instead of replacing them.
  @Mutation
  public [ADD_REQUESTS](requests: Array<Request>): void {
    const activeProject = this.activeProjectId
      ? this.projects.find((cr) => cr.id === this.activeProjectId)
      : undefined;
    if (activeProject) {
      activeProject.requests = [...activeProject.requests, ...requests];
    }
  }

  @Mutation
  public [SET_REQUEST_PENDING](Request: Request): void {
    //
    // Set the Request pending flag
    Request.isPending = true;
  }

  // Delete a Request (found by it's id)
  // from the store completely.
  @Mutation
  public [DELETE_REQUEST](id: string): void {
    const activeProject = this.activeProjectId
      ? this.projects.find((cr) => cr.id === this.activeProjectId)
      : undefined;
    if (!activeProject) return;
    let index = activeProject.requests.findIndex((c) => c.id === id);

    // if the deleted Request === activeRequestId: set to ''
    if (id === this.activeRequestId) {
      this.activeRequestId = "";
    }

    if (index > -1) {
      activeProject.requests.splice(index, 1);
    }
  }

  // Add result to the Request
  @Mutation
  public [ADD_REQUEST_RESULT](postModel: {
    id: any;
    hasResults: any;
    results: any;
  }): void {
    const activeProject = this.activeProjectId
      ? this.projects.find((cr) => cr.id === this.activeProjectId)
      : undefined;
    if (!activeProject) return;

    let request = activeProject.requests.find((c) => c.id === postModel.id);

    if (!request) {
      return;
    }

    if (postModel.hasResults) {
      request.results = [];
      postModel.results.forEach((result: any) => {
        if (request) {
          result.httpMethod = request.httpMethod;
          request.results.push(new Response(result));
        }
      });
      request.hasResults = true;
    } else {
      request.hasResults = false;
    }
  }

  @Mutation
  public [SET_ACTIVE_REQUEST_ID](activeRequestId: string) {
    this.activeRequestId = activeRequestId;
  }

  //
  // Set the Request ready flags
  @Mutation
  public [SET_REQUEST_READY](Request: Request): void {
    Request.isReady = true;
    Request.isPending = false;
  }

  @Mutation
  public [SET_HTTP_METHOD](postModel: { id: string; httpMethod: string }) {
    const activeProject = this.activeProjectId
      ? this.projects.find((cr) => cr.id === this.activeProjectId)
      : undefined;
    if (!activeProject) return;

    let Request = activeProject.requests.find((cr) => cr.id === postModel.id);

    if (Request) {
      Request.httpMethod = postModel.httpMethod;
    }
  }

  @Mutation
  public [SET_QUEUE_BUSY](postModel: boolean): void {
    this.queueBusy = postModel;
  }

  //
  // postModel should contain:
  // id of Request
  // new url of Request
  @Mutation
  public [UPDATE_URL](postModel: { id: string; url: string }) {
    const activeProject = this.activeProjectId
      ? this.projects.find((cr) => cr.id === this.activeProjectId)
      : undefined;
    if (!activeProject) return;

    let Request = activeProject.requests.find((cr) => cr.id === postModel.id);

    if (Request) {
      Request.url = postModel.url;
    }
  }
  //#endregion
}
